import Model, { attr, belongsTo } from '@ember-data/model';
import { service } from '@ember/service';
// @ts-expect-error: TODO: No types available for this package
import type IntlService from '@ember-intl/services/intl';
import type AccountModel from './account.ts';
import { Type } from '@warp-drive/core-types/symbols';

interface LoanObservationSignature {
  'balance-date': string;
  'interest-balance': number;
  'interest-rate': number;
  'loan-class': string;
  'loan-name': string;
  'next-payment-amount': number;
  'next-payment-date': string;
  'original-amount': number; // dollars
  'original-date': string;
  'parent-plus': string;
  'principal-balance': number; // dollars
  'principal-balance-amount': number; // cents
  'repayment-plan': string;
  'raw-loan-type': string;
  'loan-type': string;
  'repayment-plan-derived': string;
  'outstanding-interest-balance': number; // cents
  'forgiveness-program-qualified': number;
  'is-paid-off': boolean;
  at: string;
  balance: number;
  status: string;
}

export default class LoanModel extends Model {
  @service declare intl: IntlService;

  @attr declare number: string;
  @attr declare deactivatedOn: string;
  @attr declare observation: LoanObservationSignature;
  @attr declare observationOverrides: Record<string, unknown>[];
  @attr declare isActive: boolean;
  @attr declare originalDisbursementAmount: number;
  @attr declare originalDisbursementDate: string;
  @attr declare derivedType: string;

  // Loan-type resolution snapshot exposed by `Api::V1::LoanResource`.
  // Powers the per-loan accept/reject UI in the admin accounts drawer:
  // a loan with `resolutionSource === 'opensearch'` and no `adminAction`
  // is awaiting human confirmation. `adminAction === 'confirmed' |
  // 'rejected'` reflects a prior decision (badge state).
  @attr declare resolutionSource: 'regex' | 'opensearch' | 'admin' | null;
  @attr declare adminAction: 'confirmed' | 'rejected' | null;

  // Id of the latest LoanType `Resolution` for this loan, or null when
  // the matcher hasn't produced one yet. Lets the F5 per-loan UI route
  // confirm/reject through the JSON:API `loan-resolution` resource
  // (PATCH /api/v1/loan-resolutions/:id) so the store stays coherent
  // with the F1 admin queue. Nullable mirrors the server contract for
  // unresolved loans.
  @attr declare loanResolutionId: string | null;

  get hasPendingResolutionReview() {
    // == null catches both an explicit null from the API and an omitted
    // attr (which Ember Data surfaces as undefined).
    return this.resolutionSource === 'opensearch' && this.adminAction == null;
  }

  get isResolutionAdminConfirmed() {
    return this.adminAction === 'confirmed';
  }

  get isResolutionAdminRejected() {
    return this.adminAction === 'rejected';
  }

  /*************************
   **  Relationships      **
   *************************/

  @belongsTo('account', { async: false, inverse: 'loans' })
  declare account: AccountModel;

  /**************************
   **  Computed Properties **
   **************************/

  get currentBalance() {
    return this.observation?.balance || 0;
  }

  get currentBalanceDollars() {
    return this.observation?.balance / 100 || 0;
  }

  get interestRate() {
    return this.observation?.['interest-rate'];
  }

  get interestBalance() {
    return this.observation?.['interest-balance'] || 0;
  }

  get principalBalance() {
    return this.observation?.['principal-balance'] || 0;
  }

  get originalAmount() {
    return this.observation?.['original-amount'];
  }

  get originalDate() {
    return this.observation?.['original-date'];
  }

  get nextPaymentAmount() {
    return this.observation?.['next-payment-amount'];
  }

  get nextPaymentDate() {
    return this.observation?.['next-payment-date'];
  }

  get lastUpdatedOn() {
    return this.observation?.at;
  }

  get hasNsldsPslfData() {
    const qualifyingPayments = this.observation?.['forgiveness-program-qualified'];
    return qualifyingPayments !== undefined && qualifyingPayments !== null;
  }

  get nsldsUpdatedAt() {
    return this.observation?.at ?? this.observation?.['balance-date'] ?? null;
  }

  get repaymentPlan() {
    return this.observation?.['repayment-plan-derived'];
  }

  get status() {
    return this.observation?.status;
  }

  get loanClass() {
    return this.observation?.['loan-class'];
  }

  get name() {
    return this.observation?.['loan-name'];
  }

  get currentBalanceFormatted() {
    const dollars = this.currentBalance;
    return this.intl.formatNumber(dollars, {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    });
  }

  get loanTypeDisplayName() {
    return this.observation?.['raw-loan-type'];
  }

  get isValidForProjections() {
    const { observation, interestRate, principalBalance, nextPaymentAmount, nextPaymentDate } =
      this;
    if (!observation) {
      return false;
    }

    return principalBalance && interestRate && nextPaymentAmount && nextPaymentDate;
  }

  [Type] = 'loan' as const;
}
